/**
 * Armar una cotizacion (COT-01): el cliente, los productos de la tienda con su
 * cantidad, un descuento para toda la cotizacion, la validez y las
 * observaciones. Los totales se ven mientras se arma, con la misma cuenta que
 * usa el PDF.
 *
 * Lo que sube al servidor son los productos y las cantidades, sin precio: el
 * precio lo pone el servidor desde el catalogo.
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import type { CrearCotizacionDto } from '@bandu/compartido';

import {
  cantidadDe,
  lineaDesdeProducto,
  observacionesPorDefecto,
  totalesCotizacion,
} from '../../utilidades/cotizacion';
import type { LineaCotizacion } from '../../utilidades/cotizacion';
import { rutEsValido } from '../../utilidades/rut';
import { Icono } from '../comunes/Icono';
import { BuscadorProductoCotizacion } from './BuscadorProductoCotizacion';
import { CamposClienteCotizacion } from './CamposClienteCotizacion';
import type { ValoresCliente } from './CamposClienteCotizacion';
import { LineasCotizacion } from './LineasCotizacion';
import { PanelAdmin } from './PanelAdmin';
import { TotalesCotizacion } from './TotalesCotizacion';

export interface ValoresCotizacion {
  cliente: ValoresCliente;
  lineas: LineaCotizacion[];
  descuento: string;
  validez: string;
  observaciones: string;
}

export interface FormularioCotizacionProps {
  inicial: ValoresCotizacion;
  enviando: boolean;
  falla: string | null;
  onGuardar(datos: CrearCotizacionDto): void;
}

function enteroDe(texto: string): number | null {
  if (texto.trim() === '') return null;
  const valor = Number(texto);
  return Number.isInteger(valor) ? valor : null;
}

/** Vacio vale como ausente: el PDF no imprime un "RUT:" sin nada. */
function opcional(texto: string): string | null {
  const limpio = texto.trim();
  return limpio.length > 0 ? limpio : null;
}

export function FormularioCotizacion({ inicial, enviando, falla, onGuardar }: FormularioCotizacionProps) {
  const id = useId();
  const [cliente, setCliente] = useState(inicial.cliente);
  const [lineas, setLineas] = useState(inicial.lineas);
  const [descuento, setDescuento] = useState(inicial.descuento);
  const [validez, setValidez] = useState(inicial.validez);
  const [observaciones, setObservaciones] = useState(inicial.observaciones);
  const [problema, setProblema] = useState<string | null>(null);

  const descuentoPorcentaje = enteroDe(descuento) ?? 0;
  const validezDias = enteroDe(validez);
  const totales = totalesCotizacion(
    lineas.map((linea) => ({ precioUnitario: linea.precioUnitario, cantidad: cantidadDe(linea) })),
    descuentoPorcentaje,
  );

  function cambiarValidez(texto: string): void {
    // Las observaciones dicen la validez: si siguen siendo las de siempre, la
    // acompañan. Si alguien ya las reescribio, no se tocan.
    const anterior = enteroDe(validez);
    const nueva = enteroDe(texto);
    if (anterior !== null && nueva !== null && observaciones === observacionesPorDefecto(anterior)) {
      setObservaciones(observacionesPorDefecto(nueva));
    }
    setValidez(texto);
  }

  function validarAntes(): string | null {
    if (cliente.nombre.trim().length < 2) return 'Escribe a quién va dirigida la cotización.';
    if (cliente.rut.trim() !== '' && !rutEsValido(cliente.rut)) return 'El RUT del cliente no es válido.';
    if (lineas.length === 0) return 'Agrega al menos un producto.';
    if (lineas.some((linea) => cantidadDe(linea) === 0)) return 'Cada producto necesita una cantidad mayor que cero.';
    const porcentaje = enteroDe(descuento);
    if (descuento.trim() !== '' && (porcentaje === null || porcentaje < 0 || porcentaje > 100)) {
      return 'El descuento va de 0 a 100%.';
    }
    if (validezDias === null || validezDias < 1 || validezDias > 365) return 'La validez va de 1 a 365 días.';
    return null;
  }

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const motivo = validarAntes();
    setProblema(motivo);
    if (motivo !== null || validezDias === null) return;

    onGuardar({
      cliente: {
        nombre: cliente.nombre.trim(),
        rut: opcional(cliente.rut),
        contacto: opcional(cliente.contacto),
        telefono: opcional(cliente.telefono),
        email: opcional(cliente.email),
      },
      items: lineas.map((linea) => ({ productoId: linea.productoId, cantidad: cantidadDe(linea) })),
      descuentoPorcentaje,
      validezDias,
      observaciones: opcional(observaciones),
    });
  }

  const mensaje = problema ?? falla;

  return (
    <form className="formulario-admin formulario-cotizacion" onSubmit={enviar} noValidate>
      <PanelAdmin titulo="Cliente" bajada="Lo que va en «Facturado a»">
        <CamposClienteCotizacion
          valores={cliente}
          onCambiar={(campo, valor) => {
            setCliente((actual) => ({ ...actual, [campo]: valor }));
          }}
        />
      </PanelAdmin>

      <PanelAdmin titulo="Productos" bajada="Con el precio de la tienda, IVA incluido">
        <BuscadorProductoCotizacion
          agregados={new Set(lineas.map((linea) => linea.productoId))}
          onAgregar={(producto) => {
            setLineas((actuales) => [...actuales, lineaDesdeProducto(producto)]);
          }}
        />
        <LineasCotizacion
          lineas={lineas}
          onCantidad={(productoId, cantidad) => {
            setLineas((actuales) =>
              actuales.map((linea) => (linea.productoId === productoId ? { ...linea, cantidad } : linea)),
            );
          }}
          onQuitar={(productoId) => {
            setLineas((actuales) => actuales.filter((linea) => linea.productoId !== productoId));
          }}
        />
      </PanelAdmin>

      <PanelAdmin titulo="Condiciones">
        <div className="campos-tres">
          <div className="campo">
            <label htmlFor={`${id}-descuento`}>Descuento (%)</label>
            <input
              id={`${id}-descuento`}
              inputMode="numeric"
              value={descuento}
              onChange={(evento) => {
                setDescuento(evento.target.value.replace(/\D/g, '').slice(0, 3));
              }}
              placeholder="0"
            />
            <p className="ayuda-campo">Sobre toda la cotización.</p>
          </div>
          <div className="campo">
            <label htmlFor={`${id}-validez`}>Validez (días)</label>
            <input
              id={`${id}-validez`}
              inputMode="numeric"
              value={validez}
              onChange={(evento) => {
                cambiarValidez(evento.target.value.replace(/\D/g, '').slice(0, 3));
              }}
            />
          </div>
        </div>

        <div className="campo">
          <label htmlFor={`${id}-observaciones`}>Observaciones</label>
          <textarea
            id={`${id}-observaciones`}
            rows={4}
            value={observaciones}
            onChange={(evento) => {
              setObservaciones(evento.target.value);
            }}
            maxLength={2000}
          />
          <p className="ayuda-campo">Vacío, el PDF va sin el recuadro de observaciones.</p>
        </div>

        <TotalesCotizacion totales={totales} descuentoPorcentaje={descuentoPorcentaje} />
      </PanelAdmin>

      {mensaje !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{mensaje}</span>
        </p>
      ) : null}

      <div className="acciones-cotizacion">
        <button type="submit" className="btn btn-primario" disabled={enviando}>
          <Icono nombre="factura" />
          {enviando ? 'Guardando…' : 'Guardar y descargar PDF'}
        </button>
      </div>
    </form>
  );
}
